/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Button, Modal } from 'react-bootstrap'
import { Field, Form, Formik } from 'formik'
import Toast from 'components/atoms/Toast'
import * as Yup from 'yup'
import Select from 'react-select'
import styled from '@emotion/styled'
import { useDebounce } from 'use-debounce'

import { useLabPermission } from 'hooks/useLabPermission'
import { confirm } from 'components/molecules/Confirmation'
import DashboardContent from '../atoms/DashboardContent'
import ReadOnlyFieldset from 'components/atoms/ReadOnlyFieldset'
import { OfficePreferencesPanel } from './OfficePreferencesPanel'
import { OfficeBillingTypeSection } from './OfficeBillingTypeSection'
import states from 'constants/states'
import { UserPermission } from '../user/permissions'
import { PopoverWrapper } from 'components/atoms/PopoverWrapper'
import MaskInput from 'components/atoms/MaskInput'
import { CheckboxInput } from 'components/atoms/Checkbox'
import GoogleAutocomplete from 'components/atoms/GoogleAutocomplete'
import { phoneNumberMask, zipMask } from 'helpers/masks'
import {
  useCreateOfficeMutation,
  useGetCompanyQuery,
  useGetLabOfficeQuery,
  useGetLabOfficesQuery,
  useMergeOfficeMutation,
  useUpdateOfficeAvailableTestsMutation,
  useUpdateOfficeMutation,
} from 'store/services/office'
import { useGetLabDmeUsersQuery } from 'store/services/users'
import DataTable from 'components/atoms/DataTable'
import { getAddressFields2 } from 'components/dme/forms/utils/userFormUtils'
import * as generalHelper from 'helpers/generalHelper'
import routes from '../constants/routes'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { TableWrapper } from 'components/atoms/TableWrapper'
import { phoneValidation, requiredPhoneValidation } from 'helpers/validate'
import { useImpersonation } from 'hooks/useImpersonation'
import type { Office } from 'store/types'

const PAGE_SIZE = 10

const StyledTableWrapper = styled(TableWrapper)`
  th {
    padding: 0 0.2rem 0.4rem 0.1rem;
  }
  td {
    vertical-align: middle;
    padding: 0.2rem;
    div {
      span {
        font-size: 0.9rem;
      }
    }
  }
`
const CellWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 40px;
  justify-content: center;
  > div {
    white-space: nowrap;
    display: flex;
  }
`

const CheckboxDescription = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-left: 1.5rem;
  margin-top: -0.25rem;
  margin-bottom: 0.5rem;
  max-width: 80%;
`

const officeValidation = Yup.object().shape({
  officeName: Yup.string().required('Required'),
  officeAlias: Yup.string(),
  clientNote: Yup.string(),
  telephone: requiredPhoneValidation,
  notificationsNumber: phoneValidation,
  notificationsEmail: Yup.string().email('Invalid Email'),
  fax: requiredPhoneValidation,
  npi: Yup.string().test(
    'len',
    'Must be exactly 10 characters',
    (val?: string) => !val || val?.length === 10,
  ),
  region: Yup.string(),
  officeNumber: Yup.string(),
  addressStreetLine1: Yup.string().required('Required'),
  addressCity: Yup.string().required('Required'),
  addressState: Yup.string().required('Required'),
  addressZip: Yup.string().required('Required'),
})

interface FormValues {
  officeName: string
  officeAlias: string
  clientNote: string
  telephone: string
  fax: string
  npi: string
  region: string
  officeNumber: string
  addressStreetLine1: string
  addressStreetLine2: string
  notificationsEmail: string
  notificationsNumber: string
  addressCity: string
  addressState: string
  addressZip: string
  address: string
  btBranchId?: { siteNickname: string; branchId: string }[]
  iRhythmOfficeId?: string
  includePapScript?: boolean
  includeDmeInfoInPap?: boolean
  isDmeOffice?: boolean
}

interface RegionTreeNode {
  uuid: string
  regionName: string
  parentUuid?: string | null
  children?: RegionTreeNode[]
}

interface RegionOption {
  label: string
  value: string
  isDisabled?: boolean
}

const initialValues: FormValues = {
  officeName: '',
  officeAlias: '',
  clientNote: '',
  telephone: '',
  fax: '',
  npi: '',
  region: '',
  officeNumber: '',
  addressStreetLine1: '',
  addressStreetLine2: '',
  notificationsEmail: '',
  notificationsNumber: '',
  addressCity: '',
  addressState: '',
  addressZip: '',
  address: '',
  btBranchId: [],
  iRhythmOfficeId: '',
  includePapScript: true,
  includeDmeInfoInPap: true,
  isDmeOffice: true,
}

const formatOfficeConflictLocation = (office: Office, companyName?: string) => {
  const officeLabel = office.officeName || office.officeAlias || `Office #${office.id}`
  const deletedSuffix = office.deletedAt ? ' [deleted]' : ''
  const companySuffix = companyName ? ` in ${companyName}` : ''

  return `${officeLabel} (ID ${office.id})${companySuffix}${deletedSuffix}`
}

const buildNpiConflictMessage = (offices: Office[], companyName?: string) => {
  if (!offices.length) return ''
  if (offices.length === 1) {
    return `This NPI is already in use by ${formatOfficeConflictLocation(offices[0], companyName)}.`
  }

  return `This NPI is already in use by: ${offices.map((office) => formatOfficeConflictLocation(office, companyName)).join('; ')}.`
}

const isNpiConflictMessage = (message?: string) =>
  Boolean(message?.includes('already in use by') || message?.includes('already taken'))

const showNpiConflictModal = async (message: string) => {
  await confirm('', {
    title: 'NPI Already In Use',
    yesAction: 'Close',
    noAction: '',
    size: 'lg',
    description: () => <div>{message}</div>,
  }).catch(() => {})
}

const getLeafRegionOptions = (
  regions: RegionTreeNode[] | undefined,
  currentRegion?: { uuid?: string; regionName?: string | null },
): RegionOption[] => {
  const options: RegionOption[] = []

  const visit = (region: RegionTreeNode, path: string[]) => {
    const currentPath = [...path, region.regionName]

    if (region.children?.length) {
      region.children.forEach((child) => visit(child, currentPath))
      return
    }

    options.push({
      label: currentPath.join(' / '),
      value: region.uuid,
    })
  }

  regions?.forEach((region) => visit(region, []))

  if (currentRegion?.uuid && !options.some((option) => option.value === currentRegion.uuid)) {
    options.unshift({
      label: `${currentRegion.regionName || 'Current region'} (current non-leaf assignment)`,
      value: currentRegion.uuid,
      isDisabled: true,
    })
  }

  return options
}

const OfficeUpsert = () => {
  const navigate = useNavigate()
  const { officeId } = useParams()
  const [searchParams] = useSearchParams()
  const canEditOffices = useLabPermission(UserPermission.EDIT_OFFICES)
  const canMergeDmeOffices = useLabPermission(UserPermission.MERGE_DME_OFFICES)
  const { impersonate } = useImpersonation()
  const [currentPage, setCurrentPage] = useState(0)
  const [showMergeModal, setShowMergeModal] = useState(false)
  const [mergeOfficeSearch, setMergeOfficeSearch] = useState('')
  const [mergeConfirmed, setMergeConfirmed] = useState(false)
  const [npiLookupValue, setNpiLookupValue] = useState('')
  const [selectedMergeOffice, setSelectedMergeOffice] = useState<{
    value: number
    label: string
  } | null>(null)
  const [debouncedMergeOfficeSearch] = useDebounce(mergeOfficeSearch, 300)
  const [debouncedNpiLookupValue] = useDebounce(npiLookupValue, 300)
  const [availableTests, setAvailableTests] = useState({
    COX: false,
    POX: false,
    HST: false,
    PSG: false,
  })
  const [createOffice] = useCreateOfficeMutation()
  const [mergeOffice, { isLoading: mergeOfficeLoading }] = useMergeOfficeMutation()
  const [updateOffice] = useUpdateOfficeMutation()
  const [updateOfficeAvailableTests] = useUpdateOfficeAvailableTestsMutation()
  const { data: officeData } = useGetLabOfficeQuery(officeId ? { id: Number(officeId) } : skipToken)
  const companyId = searchParams?.get('companyId') || officeData?.companyId
  const companyIdNumber = companyId ? Number(companyId) : undefined
  const normalizedNpiLookupValue = debouncedNpiLookupValue.trim()
  const { data: companyData } = useGetCompanyQuery(companyId ? { id: companyId } : skipToken)
  const { data: mergeOfficeChoices = [], isFetching: mergeOfficeChoicesLoading } =
    useGetLabOfficesQuery(
      showMergeModal && companyId
        ? {
            companyId: companyIdNumber,
            name: debouncedMergeOfficeSearch || undefined,
          }
        : skipToken,
    )
  const { data: officesWithMatchingNpi = [] } = useGetLabOfficesQuery(
    companyIdNumber && normalizedNpiLookupValue.length === 10
      ? {
          companyId: companyIdNumber,
          npi: normalizedNpiLookupValue,
          includeDeleted: true,
        }
      : skipToken,
  )
  const { data: usersData, isLoading } = useGetLabDmeUsersQuery(
    officeId
      ? {
          officeId,
          size: PAGE_SIZE,
          page: currentPage,
        }
      : skipToken,
  )

  console.log(`!!! OFFICE ID: ${officeId}`)

  const companyName = companyData?.companyName
  const canShowMergeOffice = Boolean(officeId && officeData?.isDmeOffice && canMergeDmeOffices)
  const conflictingNpiOffices = useMemo(
    () => officesWithMatchingNpi.filter((office) => Number(office.id) !== Number(officeId)),
    [officesWithMatchingNpi, officeId],
  )
  const npiConflictMessage = useMemo(
    () => buildNpiConflictMessage(conflictingNpiOffices, companyName),
    [conflictingNpiOffices, companyName],
  )

  useEffect(() => {
    setNpiLookupValue(officeData?.npi || '')
  }, [officeData?.npi])

  useEffect(() => {
    if (officeData?.availableTests) {
      setAvailableTests({
        POX: officeData?.availableTests?.POX?.available,
        COX: officeData?.availableTests?.COX?.available,
        HST: officeData?.availableTests?.HST?.available,
        PSG: officeData?.availableTests?.PSG?.available,
      })
    }
  }, [officeData])

  const init = useMemo(
    () =>
      officeData && {
        officeName: officeData?.officeName,
        officeAlias: officeData?.officeAlias,
        clientNote: officeData?.clientNote,
        telephone: officeData?.telephone,
        fax: officeData?.fax,
        npi: officeData?.npi || '',
        officeNumber: officeData?.officeNumber,
        address: officeData?.addressStreetLine1,
        addressStreetLine1: officeData?.addressStreetLine1,
        addressStreetLine2: officeData?.addressStreetLine2,
        addressCity: officeData?.addressCity,
        addressState: officeData?.addressState,
        addressZip: officeData?.addressZip,
        region: officeData?.region?.uuid,
        iRhythmOfficeId: officeData?.iRhythmOfficeId,
        btBranchId: officeData?.btBranchId,
        oneShipSupported: officeData?.oneShipSupported,
        notificationsReroute: officeData?.notificationsReroute,
        notificationsEmail: officeData?.notificationsEmail,
        notificationsNumber: officeData?.notificationsNumber,
        rxRecognitionEnabled: officeData?.rxRecognitionEnabled,
        rxVerificationEnabled: officeData?.rxVerificationEnabled,
        includePapScript: officeData?.includePapScript,
        includeDmeInfoInPap: officeData?.includeDmeInfoInPap,
        isDmeOffice: officeData?.isDmeOffice,
      },
    [officeData],
  )

  const regions = useMemo(
    () => getLeafRegionOptions(companyData?.regions, officeData?.region),
    [companyData?.regions, officeData?.region],
  )
  const mergeOfficeOptions = useMemo(
    () =>
      mergeOfficeChoices
        .filter((office) => office.isDmeOffice && Number(office.id) !== Number(officeId))
        .map((office) => ({
          value: Number(office.id),
          label: `${office.officeName || office.officeAlias || `Office #${office.id}`} (${office.addressCity || 'Unknown city'}, ${office.addressState || 'Unknown state'})`,
        })),
    [mergeOfficeChoices, officeId],
  )

  // TES-2881: without EDIT_OFFICES, render the office read-only instead of bouncing.
  const readOnly = !canEditOffices

  const closeMergeModal = () => {
    setShowMergeModal(false)
    setSelectedMergeOffice(null)
    setMergeOfficeSearch('')
    setMergeConfirmed(false)
  }

  const handleMergeOffice = async () => {
    if (!officeId || !selectedMergeOffice) {
      return
    }

    try {
      await mergeOffice({
        sourceOfficeId: Number(officeId),
        targetOfficeId: selectedMergeOffice.value,
      }).unwrap()
      Toast({ type: 'success', label: 'Office merged successfully' })
      const targetOfficeId = selectedMergeOffice.value
      closeMergeModal()
      navigate(`${routes.index}${routes.office.root}${routes.upsert}${targetOfficeId}`)
    } catch (error) {
      const errorMessage = (error as { data?: { message?: string } })?.data?.message
      if (errorMessage) {
        Toast({ type: 'error', label: errorMessage })
      }
    }
  }

  const employeesHeader = [
    {
      Header: 'ID',
      accessor: 'userId',
      Cell: ({ row }) => row.original.id,
    },
    {
      Header: 'Name',
      accessor: 'userName',
      columnSize: 3,
      Cell: ({ row }) => (
        <>
          <Link to={`${routes.index}${routes.dmeUsers.root}${routes.upsert}${row.original.id}`}>
            {`${_.capitalize(row.original.lastName)}${
              row.original.suffix ? ' ' + row.original.suffix : ''
            }, ${_.capitalize(row.original.firstName)} ${
              row.original.middleName ? _.capitalize(row.original.middleName) : ''
            }`}
          </Link>
        </>
      ),
    },
    {
      Header: 'Role',
      accessor: 'role',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <div>{row.original.roles?.[0]?.replace('_', ' ')}</div>
        </CellWrapper>
      ),
    },
    {
      Header: 'User Type',
      accessor: 'officeUserType',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <div>{_.capitalize(row.original.officeUserType)}</div>
        </CellWrapper>
      ),
    },
    {
      Header: 'Admin',
      accessor: 'isAdmin',
      columnSize: 3,
      Cell: ({ row }) => (
        <CellWrapper>
          <div css={{ margin: 'auto' }}>
            {row.original.officeUserType === 'PRIMARY' && row.original.adminOf ? (
              <i className="fas fa-check-circle" />
            ) : row.original.adminOf === 'ASSIGNED_OFFICES' ? (
              <i className="fas fa-check-circle" />
            ) : null}
          </div>
        </CellWrapper>
      ),
    },
  ]

  return (
    <div>
      <DashboardContent
        icon="fas fa-building"
        title={officeId ? (readOnly ? 'View Office' : 'Edit Office') : 'Create Office'}
        subtitle={
          <div>
            <Link to={`${routes.index}${routes.company.root}${routes.upsert}${companyId}`}>
              {`< Back to ${companyName}`}
            </Link>
            {officeId && (
              <span className="float-right">
                {canShowMergeOffice && !readOnly && (
                  <Button
                    variant="outline-danger"
                    className="ml-2"
                    size="sm"
                    onClick={() => setShowMergeModal(true)}>
                    <i className="fas fa-code-branch mr-2" />
                    Merge Office
                  </Button>
                )}
                <PopoverWrapper
                  placement="left"
                  customContent={<div className="p-2">Impersonate this DME office</div>}>
                  <Button
                    variant="primary"
                    className="ml-2"
                    css={{ minWidth: '40px' }}
                    size="sm"
                    onClick={async (e) => {
                      e.stopPropagation()
                      impersonate(Number(officeId))
                    }}>
                    <i className="fas fa-external-link-alt mr-2" />
                    Impersonate
                  </Button>
                </PopoverWrapper>
              </span>
            )}
          </div>
        }
        breadcrumb={[
          {
            label: 'Companies',
            to: `${routes.index}${routes.company.root}`,
          },
          {
            label: companyName,
            to: companyId
              ? `${routes.index}${routes.company.root}${routes.upsert}${companyId}`
              : `${routes.index}${routes.company.root}`,
          },
          { label: officeId ? 'Edit' : 'New' },
        ]}
        content={
          <>
            <div className="card-body row">
              <div className="col-md-12 col-lg-6 mb-3">
                <div className="card card-primary p-2">
                  <div
                    className="card-header"
                    css={{
                      color: 'black !important',
                      backgroundColor: 'transparent !important',
                    }}>
                    <h3 className="card-title">OFFICE INFORMATION</h3>
                  </div>
                  <Formik
                    enableReinitialize={true}
                    validationSchema={officeValidation}
                    initialValues={init ?? initialValues}
                    onSubmit={async (values) => {
                      try {
                        if (conflictingNpiOffices.length) {
                          await showNpiConflictModal(npiConflictMessage)
                          return
                        }

                        const { address, nickname, branchId, ...vals } = values
                        if (!vals.region) delete vals.region
                        vals.availableTests = availableTests
                        if (vals.btBranchId?.length) {
                          vals.availableTests.POX = true
                          vals.availableTests.COX = true
                        }
                        if (officeId) {
                          await updateOffice({
                            id: officeId,
                            ...vals,
                          }).unwrap()
                          Toast({ type: 'success', label: `Office updated!` })
                        } else {
                          const newOffice = await createOffice({
                            companyId: Number(companyId),
                            ...vals,
                          }).unwrap()
                          navigate(
                            `${routes.index}${routes.office.root}${routes.upsert}${newOffice.id}`,
                          )
                          Toast({ type: 'success', label: `Office created!` })
                        }
                      } catch (err) {
                        if (isNpiConflictMessage(err.data.message)) {
                          await showNpiConflictModal(err.data.message)
                          return
                        }

                        Toast({ type: 'error', label: err.data.message })
                      }
                    }}>
                    {({ values, errors, touched, handleChange, setFieldValue }) => (
                      <Form id="officeUpsert" autoComplete="one-time-code">
                        <ReadOnlyFieldset readOnly={readOnly}>
                        <div className={`container bg-white mb-3 px-1 p-0`}>
                          <div className={`container pt-3 pb-1`}>
                            <div className="row">
                              <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                <label htmlFor="officeName">
                                  Office Name
                                  <span className="text-danger">*</span>
                                </label>
                                <Field
                                  type="text"
                                  name="officeName"
                                  className="form-control"
                                  placeholder="Office Name"
                                  value={values.officeName}
                                  onChange={handleChange}
                                />
                                {touched.officeName && errors.officeName && (
                                  <div className="text-danger">{errors.officeName}</div>
                                )}
                              </div>
                              <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                <label htmlFor="telephone">
                                  Telephone <span className="text-danger">*</span>
                                </label>
                                <MaskInput
                                  value={values.telephone}
                                  onChange={handleChange}
                                  name="telephone"
                                  mask={phoneNumberMask}
                                  placeholder="(###) ###-####"
                                  autoComplete="off"
                                />
                                {touched.telephone && errors.telephone && (
                                  <div className="text-danger">{errors.telephone}</div>
                                )}
                              </div>
                              <div className="col-12 col-md-4 form-group textGray">
                                <label htmlFor="lastName">
                                  Fax
                                  <span className="text-danger">*</span>
                                </label>
                                <MaskInput
                                  value={values.fax}
                                  onChange={handleChange}
                                  name="fax"
                                  mask={phoneNumberMask}
                                  placeholder="(###) ###-####"
                                  autoComplete="off"
                                />
                                {touched.fax && errors.fax && (
                                  <div className="text-danger">{errors.fax}</div>
                                )}
                              </div>
                            </div>
                            <div className="row">
                              <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                <label htmlFor="officeAlias">Office Alias</label>
                                <Field
                                  type="text"
                                  name="officeAlias"
                                  className="form-control"
                                  placeholder="Office Alias"
                                  value={values.officeAlias}
                                  onChange={handleChange}
                                />
                                {touched.officeAlias && errors.officeAlias && (
                                  <div className="text-danger">{errors.officeAlias}</div>
                                )}
                              </div>
                              <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                <label htmlFor="npi">NPI</label>
                                <Field
                                  type="text"
                                  name="npi"
                                  maxlength="10"
                                  className="form-control"
                                  placeholder="NPI"
                                  value={values.npi}
                                  onChange={(e) => {
                                    handleChange(e)
                                    setNpiLookupValue(e.target.value)
                                  }}
                                />
                                {touched.npi && errors.npi && (
                                  <div className="text-danger">{errors.npi}</div>
                                )}
                                {!errors.npi && npiConflictMessage && (
                                  <div className="alert alert-warning py-2 px-3 mt-2 mb-0">
                                    {npiConflictMessage}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-12 form-group textGray">
                                <label htmlFor="clientNote">Client Note</label>
                                <Field
                                  as="textarea"
                                  name="clientNote"
                                  rows={3}
                                  className="form-control"
                                  placeholder="Client Note"
                                  value={values.clientNote}
                                  onChange={handleChange}
                                />
                                {touched.clientNote && errors.clientNote && (
                                  <div className="text-danger">{errors.clientNote}</div>
                                )}
                              </div>
                            </div>
                            <div className="row mt-3">
                              <div className="col-12 form-group textGray">
                                <label htmlFor="region">Region</label>
                                <Select
                                  className="basic-single w-100 mr-3 mb-3"
                                  css={{
                                    '& .select__control': {
                                      height: '34px !important',
                                      minHeight: 'unset',
                                    },
                                  }}
                                  classNamePrefix="select"
                                  isClearable
                                  isSearchable
                                  placeholder="Select Region"
                                  name="region"
                                  autoComplete="region"
                                  onChange={(item) => setFieldValue('region', item?.value ?? '')}
                                  options={regions}
                                  value={regions?.find((i) => i.value === values.region) ?? null}
                                />
                              </div>
                            </div>
                          </div>
                          <div
                            className="row mx-2 mb-2"
                            css={{
                              border: '1px solid #ccc',
                              borderRadius: '5px',
                              padding: '5px 10px',
                            }}>
                            <div className="col-6 form-group textGray">
                              <label htmlFor="iRhythmOfficeId">IRhythm Office ID</label>
                              <div className="d-flex align-items-center">
                                <Field
                                  type="text"
                                  placeholder="IRhythm Office ID"
                                  name="iRhythmOfficeId"
                                  className="form-control"
                                  value={values.iRhythmOfficeId}
                                  onChange={handleChange}
                                />
                              </div>
                            </div>
                          </div>

                          <div
                            className="row mx-2 mb-2"
                            css={{
                              border: '1px solid #ccc',
                              borderRadius: '5px',
                              padding: '5px 10px',
                            }}>
                            <div className="col-6 form-group textGray">
                              <label htmlFor="npi">Brightree Branch Info</label>
                              <div className="d-flex align-items-center">
                                <Field
                                  as="select"
                                  name="nickname"
                                  className="form-control mr-2"
                                  placeholder="Site Nickname"
                                  value={values.nickname}
                                  onChange={handleChange}>
                                  <option value="" disabled selected>
                                    Nickname
                                  </option>
                                  {companyData?.btSiteNickname?.map((n) => (
                                    <option key={n} value={n}>
                                      {n}
                                    </option>
                                  ))}
                                </Field>

                                <Field
                                  type="text"
                                  name="branchId"
                                  className="form-control"
                                  placeholder="Branch ID"
                                  value={values.branchId}
                                  onChange={handleChange}
                                />
                                <i
                                  onClick={() => {
                                    if (values.nickname) {
                                      setFieldValue('btBranchId', [
                                        {
                                          siteNickname: values.nickname,
                                          branchId: values.branchId,
                                        },
                                        ...(values.btBranchId || []),
                                      ])
                                      setFieldValue('nickname', '')
                                      setFieldValue('branchId', '')
                                      setAvailableTests((p) => ({
                                        ...p,
                                        POX: true,
                                        COX: true,
                                      }))
                                    }
                                  }}
                                  className="fa fa-plus-circle ml-2"
                                  aria-hidden="true"
                                  css={{
                                    cursor: 'pointer',
                                    color: values.nickname && values.branchId ? '#2c83bd' : '#ccc',
                                  }}
                                />
                              </div>
                            </div>
                            <div className="col-6" css={{ overflowY: 'auto', maxHeight: 150 }}>
                              {!!values.btBranchId?.length && (
                                <table
                                  className="table"
                                  css={{ '& td, th': { padding: '0.25rem' } }}>
                                  <thead>
                                    <tr>
                                      <th>Site Nickname</th>
                                      <th>Branch ID</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {values.btBranchId.map((n) => (
                                      <tr key={n.branchId}>
                                        <td>{n.siteNickname}</td>
                                        <td>{n.branchId}</td>
                                        <td>
                                          <i
                                            onClick={() =>
                                              confirm('brightree branch')
                                                .then(() =>
                                                  setFieldValue(
                                                    'btBranchId',
                                                    values.btBranchId?.filter(
                                                      (b) => b.branchId !== n.branchId,
                                                    ),
                                                  ),
                                                )
                                                .catch(() => {})
                                            }
                                            className="fa fa-times-circle ml-2"
                                            aria-hidden="true"
                                            css={{ cursor: 'pointer', color: '#2c83bd' }}
                                          />
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>

                          <div className={`container py-2 px-3`}>
                            <div className="row">
                              <div className="col-12">
                                <div className="row">
                                  <div className="col-12 form-group textGray">
                                    <label htmlFor="addressStreetLine1">
                                      {generalHelper.t('Primary Address')}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <GoogleAutocomplete
                                      id="address"
                                      name="address"
                                      onInputChanged={(v) => setFieldValue('addressStreetLine1', v)}
                                      onChange={(place) => {
                                        const { address, city, state, zip } =
                                          getAddressFields2(place)
                                        console.log(address, city, state, zip)
                                        document.getElementById('address').value = address
                                        setFieldValue('addressStreetLine1', address)
                                        setFieldValue('addressCity', city)
                                        setFieldValue('addressState', state)
                                        setFieldValue('addressZip', zip)
                                      }}
                                    />
                                    {touched.addressStreetLine1 && errors.addressStreetLine1 ? (
                                      <div className="text-danger">{errors.addressStreetLine1}</div>
                                    ) : null}
                                  </div>
                                  <div className="col-12 form-group textGray">
                                    <Field
                                      type="text"
                                      name="addressStreetLine2"
                                      className="form-control"
                                      placeholder="Suite / Apt"
                                    />
                                  </div>
                                </div>
                                <div className="row">
                                  <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                    <label htmlFor="addressCity">
                                      City
                                      <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                      type="text"
                                      name="addressCity"
                                      className="form-control"
                                      placeholder="City"
                                    />
                                    {touched.addressCity && errors.addressCity ? (
                                      <div className="text-danger">{errors.addressCity}</div>
                                    ) : null}
                                  </div>
                                  <div className="col-12 col-md-4 form-group textGray mb-3 mb-md-0">
                                    <label htmlFor="addressState">
                                      State
                                      <span className="text-danger">*</span>
                                    </label>
                                    <Field
                                      className="form-control"
                                      as="select"
                                      name="addressState"
                                      placeholder="State">
                                      {states &&
                                        states.map((s, i) => (
                                          <option key={i} value={s.id}>
                                            {s.name}
                                          </option>
                                        ))}
                                    </Field>
                                    {touched.addressState && errors.addressState ? (
                                      <div className="text-danger">{errors.addressState}</div>
                                    ) : null}
                                  </div>
                                  <div className="col-12 col-md-4 form-group textGray">
                                    <label htmlFor="addressZip">
                                      Zip Code
                                      <span className="text-danger">*</span>
                                    </label>
                                    <MaskInput
                                      value={values.addressZip}
                                      onChange={handleChange}
                                      name="addressZip"
                                      mask={zipMask}
                                      placeholder="#####-####"
                                    />

                                    {touched.addressZip && errors.addressZip && (
                                      <div className="text-danger">{errors.addressZip}</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="col-sm-12 py-3 pr-0 border-top">
                            <Field
                              id="oneShipSupported"
                              name="oneShipSupported"
                              component={CheckboxInput}
                              label="OneShip Supported"
                            />
                          </div>
                          <div className="col-sm-12 py-3 pr-0 border-top">
                            <Field
                              id="rxRecognitionEnabled"
                              name="rxRecognitionEnabled"
                              component={CheckboxInput}
                              label="Enable Rx Recognition"
                            />
                            <Field
                              id="rxVerificationEnabled"
                              name="rxVerificationEnabled"
                              component={CheckboxInput}
                              label="Enable Rx Verification"
                            />
                          </div>
                          <div className="col-sm-12 py-3 pr-0 border-top">
                            <Field
                              id="notificationsReroute"
                              name="notificationsReroute"
                              component={CheckboxInput}
                              label="Re-route notifications"
                            />
                          </div>
                          {availableTests.HST && (
                            <div className="col-sm-12 py-3 pr-0 border-top">
                              <Field
                                id="isDmeOffice"
                                name="isDmeOffice"
                                component={CheckboxInput}
                                label="This is a DME Office"
                              />
                              <CheckboxDescription>
                                Enables PAP script settings, and auto-forwarding signed PAP to DME
                              </CheckboxDescription>
                              <Field
                                id="includePapScript"
                                name="includePapScript"
                                component={CheckboxInput}
                                label="Include PAP script in HST Report"
                              />
                              <CheckboxDescription>
                                Can be changed while uploading the report
                              </CheckboxDescription>
                              <Field
                                id="includeDmeInfoInPap"
                                name="includeDmeInfoInPap"
                                component={CheckboxInput}
                                label="Include DME Information in PAP script"
                                disabled={!values.includePapScript}
                              />
                              <CheckboxDescription>
                                Can be changed while uploading the report
                              </CheckboxDescription>
                            </div>
                          )}
                          {values.notificationsReroute && (
                            <div className="row">
                              <div className="col-12 col-md-5 form-group textGray mb-3 mb-md-0">
                                <label htmlFor="notificationsEmail">Notifications Email</label>
                                <Field
                                  type="text"
                                  name="notificationsEmail"
                                  className="form-control"
                                  placeholder="abc@abc.com"
                                  value={values.notificationsEmail}
                                  onChange={handleChange}
                                />
                                {touched.notificationsEmail && errors.notificationsEmail && (
                                  <div className="text-danger">{errors.notificationsEmail}</div>
                                )}
                              </div>
                              <div className="col-12 col-md-5 form-group textGray">
                                <label htmlFor="notificationsNumber">Notifications Number</label>
                                <MaskInput
                                  value={values.notificationsNumber}
                                  onChange={handleChange}
                                  name="notificationsNumber"
                                  mask={phoneNumberMask}
                                  placeholder="(###) ###-####"
                                  autoComplete="off"
                                />
                                {touched.notificationsNumber && errors.notificationsNumber && (
                                  <div className="text-danger">{errors.notificationsNumber}</div>
                                )}
                              </div>
                            </div>
                          )}
                          {!readOnly && (
                            <div className="col-sm-12 pt-3 pr-0 border-top">
                              <div className="col-sm-12 d-flex justify-content-end">
                                <Button variant="primary" type="submit">
                                  Save Changes
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                        </ReadOnlyFieldset>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
              <div className="col-md-12 col-lg-6 mb-3">
                {officeId && (
                  <>
                    <div className="card card-primary p-2">
                      <div
                          className="card-header d-flex"
                          css={{
                            color: 'black !important',
                            backgroundColor: 'transparent !important',
                            justifyContent: 'space-between',
                            '&::after': {
                              display: 'none',
                            },
                          }}>
                        <h3 className="card-title">EMPLOYEES</h3>
                        {!readOnly && (
                            <Button
                                variant="primary"
                                onClick={() =>
                                    navigate(
                                        `${routes.index}${routes.dmeUsers.root}${routes.upsert}?companyId=${officeData?.companyId}&officeId=${officeId}`,
                                    )
                                }
                                size="sm">
                              Add User
                            </Button>
                        )}
                      </div>
                      <StyledTableWrapper
                          className="card-body row mt-3"
                          style={{
                            overflowY:
                                !usersData?.items?.length || usersData?.items?.length < 10
                                    ? 'unset'
                                    : 'auto',
                          }}>
                        <DataTable
                            tableKey={'lab_office_users'}
                            data={usersData}
                            status={isLoading}
                            tableColumns={employeesHeader}
                            pageSize={PAGE_SIZE}
                            setCurrentPage={setCurrentPage}
                            setSortOptions={() => {}}
                            entityName="employees"
                            onRowClick={(row) =>
                                navigate(
                                    `${routes.index}${routes.dmeUsers.root}${routes.upsert}${row.original.id}`,
                                )
                            }
                        />
                      </StyledTableWrapper>
                    </div>
                    <div className="card card-primary p-2">
                      <div
                          className="card-header"
                          css={{
                            color: 'black !important',
                            backgroundColor: 'transparent !important',
                          }}>
                        <h3 className="card-title">FOLLOW-UP CARE PREFERENCES</h3>
                      </div>
                      <div className="card-body">
                        <ReadOnlyFieldset readOnly={readOnly}>
                          <OfficePreferencesPanel officeId={Number(officeId)} />
                        </ReadOnlyFieldset>
                      </div>
                    </div>
                    <div className="card card-primary p-2">
                      <div
                          className="card-header"
                          css={{
                            color: 'black !important',
                            backgroundColor: 'transparent !important',
                          }}>
                        <h3 className="card-title">BILLING</h3>
                      </div>
                      <div className="card-body">
                        <ReadOnlyFieldset readOnly={readOnly}>
                          <OfficeBillingTypeSection officeId={Number(officeId)} />
                        </ReadOnlyFieldset>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="card-body row pt-0">
              <div className="col-md-12 col-lg-6 mb-3">
                <div className="card card-primary p-2">
                  <div
                    className="card-header"
                    css={{
                      color: 'black !important',
                      backgroundColor: 'transparent !important',
                    }}>
                    <h3 className="card-title">AVAILABLE TESTS</h3>
                  </div>
                  <ReadOnlyFieldset readOnly={readOnly}>
                  <div className="row mt-3">
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                      <div className="col-12 form-check pb-3 ml-2">
                        <input
                          type="checkbox"
                          value="POX"
                          name="payer2"
                          className="form-check-input"
                          checked={availableTests.POX}
                          onChange={(event) =>
                            setAvailableTests({ ...availableTests, POX: event.target.checked })
                          }
                          id="pox-checkbox"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="payer2-checkbox"
                          css={{ paddingTop: '2px' }}>
                          Pulse Oximetry (POX)
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                      <div className="col-12 form-check pb-3 ml-2">
                        <input
                          type="checkbox"
                          value="COX"
                          name="payer2"
                          className="form-check-input"
                          checked={availableTests.COX}
                          onChange={(event) =>
                            setAvailableTests({ ...availableTests, COX: event.target.checked })
                          }
                          id="cox-checkbox"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="cox-checkbox"
                          css={{ paddingTop: '2px' }}>
                          Capnography (CAPOX)
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-3 mb-3 mb-md-0">
                      <div className="col-12 form-check pb-3 ml-2">
                        <input
                          type="checkbox"
                          value="HST"
                          name="payer2"
                          className="form-check-input"
                          checked={availableTests.HST}
                          onChange={(event) =>
                            setAvailableTests({ ...availableTests, HST: event.target.checked })
                          }
                          id="hst-checkbox"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="hst-checkbox"
                          css={{ paddingTop: '2px' }}>
                          Home Sleep Testing (HST)
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="col-12 form-check pb-3 ml-2">
                        <input
                          type="checkbox"
                          value="PSG"
                          name="payer2"
                          className="form-check-input"
                          checked={availableTests.PSG}
                          onChange={(event) =>
                            setAvailableTests({ ...availableTests, PSG: event.target.checked })
                          }
                          id="psg-checkbox"
                        />
                        <label
                          className="form-check-label"
                          htmlFor="psg-checkbox"
                          css={{ paddingTop: '2px' }}>
                          In-Lab Sleep Testing (PSG)
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="row justify-content-end p-3">
                    {!!officeData?.btBranchId?.length && (
                      <span css={{ position: 'absolute', left: 10, bottom: 20, opacity: 0.7 }}>
                        Brightree sites have to have POX and CAPOX enabled
                      </span>
                    )}
                    {!readOnly && (
                      <Button
                        variant="primary"
                        onClick={async () => {
                          try {
                            await updateOfficeAvailableTests({
                              id: officeId,
                              availableTests,
                            }).unwrap()
                            Toast({ type: 'success', label: `Office updated!` })
                          } catch (err) {
                            Toast({ type: 'error', label: err.data.message })
                          }
                        }}>
                        Save Changes
                      </Button>
                    )}
                  </div>
                  </ReadOnlyFieldset>
                </div>
              </div>
            </div>
          </>
        }
      />
      <Modal show={showMergeModal} onHide={closeMergeModal} centered>
        <Modal.Header>
          <Modal.Title>Merge Office</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="alert alert-warning mb-3">
            This action will move all orders, devices, DME payers, patients, physicians, and users
            from this office into the selected target office. This cannot be undone.
          </div>
          <p className="mb-3">Select the target office within the same company.</p>
          <Select
            classNamePrefix="select"
            isClearable
            isSearchable
            placeholder="Search target office"
            options={mergeOfficeOptions}
            value={selectedMergeOffice}
            onChange={(option) => setSelectedMergeOffice(option)}
            onInputChange={(value) => setMergeOfficeSearch(value)}
            isLoading={mergeOfficeChoicesLoading}
            isDisabled={mergeOfficeLoading}
          />
          <div className="form-check mt-3">
            <input
              id="confirm-office-merge"
              type="checkbox"
              className="form-check-input"
              checked={mergeConfirmed}
              onChange={(event) => setMergeConfirmed(event.target.checked)}
              disabled={mergeOfficeLoading}
            />
            <label className="form-check-label" htmlFor="confirm-office-merge">
              I understand this merge is irreversible.
            </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeMergeModal} disabled={mergeOfficeLoading}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleMergeOffice}
            disabled={!selectedMergeOffice || !mergeConfirmed || mergeOfficeLoading}>
            {mergeOfficeLoading ? 'Merging...' : 'Merge Office'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}

export default OfficeUpsert
